import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Plus, X, MessageSquare, ThumbsUp, MessageCircle } from "lucide-react";
import { useSocialSources } from "@/hooks/useContentStudio";
import type { SocialPostSource } from "@/types/content-studio";

interface SocialSourceTabProps {
  selected: SocialPostSource[];
  onAdd: (source: SocialPostSource) => void;
  onRemove: (id: string) => void;
}

export function SocialSourceTab({ selected, onAdd, onRemove }: SocialSourceTabProps) {
  const [search, setSearch] = useState("");
  const { data: posts, isLoading } = useSocialSources(50);

  const filtered = (posts || []).filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.poster_name?.toLowerCase().includes(q) ||
      p.full_content?.toLowerCase().includes(q) ||
      p.monitoring_profile?.toLowerCase().includes(q)
    );
  });

  const selectedIds = new Set(selected.map((s) => s.post_id));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder="Search social posts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 h-8 text-xs"
        />
      </div>

      <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1">
        {filtered.map((post) => {
          const isSelected = selectedIds.has(post.post_id);
          return (
            <Card
              key={post.post_id}
              className={`cursor-pointer transition-colors ${isSelected ? "border-primary bg-primary/5" : "hover:bg-muted/50"}`}
              onClick={() => (isSelected ? onRemove(post.post_id) : onAdd(post))}
            >
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-xs font-medium text-foreground">{post.poster_name || "Unknown"}</span>
                      {post.monitoring_profile && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0">{post.monitoring_profile}</Badge>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground line-clamp-3">
                      {post.content_preview || post.full_content}
                    </p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-[9px] text-muted-foreground flex items-center gap-0.5">
                        <ThumbsUp className="h-2.5 w-2.5" /> {post.likes}
                      </span>
                      <span className="text-[9px] text-muted-foreground flex items-center gap-0.5">
                        <MessageCircle className="h-2.5 w-2.5" /> {post.comments}
                      </span>
                      {post.post_date && (
                        <span className="text-[9px] text-muted-foreground">
                          {new Date(post.post_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant={isSelected ? "default" : "ghost"}
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      isSelected ? onRemove(post.post_id) : onAdd(post);
                    }}
                  >
                    {isSelected ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-30" />
            <p className="text-xs">No social posts found</p>
          </div>
        )}
      </div>
    </div>
  );
}
